import { useState, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CheckCircle2, LogIn as LogInIcon, DollarSign, Clock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { printIvBagLabel, PrintLabelButton } from "@/components/IvBagLabel";
import { CheckoutMembershipSection, EliteBadge, useActiveMemberIds } from "@/components/Membership";
import { API, formatApiError, useAuth, DELIVERY_FEE_AMOUNT } from "@/lib/shared";

export const DaySchedule = ({ appointments, patients, staff, serviceTypes, selectedDate, onRefresh }) => {
  const memberIds = useActiveMemberIds();
  const { user } = useAuth();
  const [checkoutAppt, setCheckoutAppt] = useState(null);

  const dayAppointments = useMemo(() => {
    const target = format(selectedDate, 'yyyy-MM-dd');
    return appointments
      .filter(a => a.start_datetime?.slice(0, 10) === target)
      .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
  }, [appointments, selectedDate]);

  const patientById = useMemo(() => Object.fromEntries(patients.map(p => [p.id, p])), [patients]);
  const serviceById = useMemo(() => Object.fromEntries(serviceTypes.map(s => [s.id, s])), [serviceTypes]);
  const staffById = useMemo(() => Object.fromEntries(staff.map(s => [s.id, s])), [staff]);

  const handleCheckIn = async (apt) => {
    try {
      await axios.post(`${API}/appointments/${apt.id}/check-in`);
      toast.success(`Checked in: ${patientById[apt.patient_id]?.first_name || 'patient'}`);
      onRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Check-in failed'); }
  };

  const handleUndoCheckIn = async (apt) => {
    if (!window.confirm('Undo check-in for this patient?')) return;
    try {
      await axios.post(`${API}/appointments/${apt.id}/undo-check-in`);
      toast.success('Check-in undone');
      onRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Failed'); }
  };

  const handleConfirm = async (apt) => {
    try {
      await axios.post(`${API}/appointments/${apt.id}/confirm`);
      toast.success('Appointment confirmed');
      onRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Failed'); }
  };

  return (
    <Card className="w-full max-w-2xl gold-border bg-card/80">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-primary">{format(selectedDate, 'EEEE, MMM d, yyyy')} · Schedule</h3>
          <span className="text-xs text-muted-foreground">{dayAppointments.length} appointment{dayAppointments.length === 1 ? '' : 's'}</span>
        </div>

        {dayAppointments.length === 0 && (
          <p className="text-center text-muted-foreground text-sm py-6">No appointments scheduled for this day.</p>
        )}

        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {dayAppointments.map(apt => {
            const p = patientById[apt.patient_id];
            const svc = serviceById[apt.service_type_id];
            const dt = new Date(apt.start_datetime);
            const time = dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
            const checkedIn = !!apt.checked_in_at;
            const checkedOut = !!apt.checked_out_at;
            const apptStaff = (apt.staff_ids || []).map(id => staffById[id]).filter(Boolean);

            return (
              <div key={apt.id}
                className={`p-3 rounded-lg border transition-all ${
                  checkedOut ? 'bg-emerald-500/5 border-emerald-500/30' :
                  checkedIn ? 'bg-primary/10 border-primary/40' :
                  apt.confirmed === false ? 'bg-orange-500/5 border-orange-500/50 ring-1 ring-orange-500/30' :
                  'bg-muted/30 border-border/40 hover:border-primary/40'
                }`}
                data-testid={`appt-slot-${apt.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-sm font-semibold text-foreground">{time}</span>
                      <div className="w-2 h-2 rounded-full" style={{backgroundColor: svc?.color || '#888'}} />
                      <span className="text-sm text-muted-foreground">{svc?.name || 'Service'}</span>
                      {apt.confirmed === false && (
                        <Badge variant="outline" className="text-[9px] border-orange-500/50 text-orange-300 bg-orange-500/15 animate-pulse" data-testid={`pending-badge-${apt.id}`}>
                          ⚠ PENDING · From Website
                        </Badge>
                      )}
                    </div>
                    <div className="text-base font-bold mt-1 text-foreground">
                      {p ? `${p.first_name} ${p.last_name}` : 'Unknown patient'}
                      {p && memberIds.has(p.id) && <EliteBadge className="ml-2" testId={`appt-elite-${apt.id}`} />}
                      {p?.intake_completed_at && (
                        <Badge variant="outline" className="ml-2 text-[9px] border-emerald-500/50 text-emerald-300 bg-emerald-500/10" data-testid={`intake-done-${apt.id}`}>
                          ✓ Intake
                        </Badge>
                      )}
                      {p && !p.intake_completed_at && (
                        <Badge variant="outline" className="ml-2 text-[9px] border-amber-500/40 text-amber-300/80 bg-amber-500/5">
                          ⏳ Intake pending
                        </Badge>
                      )}
                    </div>
                    {(apt.add_on_service_ids || []).length > 0 && (
                      <p className="text-xs mt-0.5 text-primary/90" data-testid={`apt-addons-${apt.id}`}>
                        <span className="text-[9px] uppercase tracking-wider text-muted-foreground mr-1">+ Add-ons</span>
                        {apt.add_on_service_ids.map(id => serviceById[id]?.name).filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {apt.series_id && (
                      <p className="text-[10px] mt-0.5 text-muted-foreground flex items-center gap-1" data-testid={`apt-series-${apt.id}`}>
                        <RefreshCw className="w-3 h-3 text-primary/70" />Repeat visit {apt.series_index}/{apt.series_total} · {apt.series_frequency === 'biweekly' ? 'every 2 weeks' : apt.series_frequency}
                      </p>
                    )}
                    {p?.phone && <p className="text-xs text-muted-foreground">{p.phone}</p>}
                    {apt.service_address && (
                      <p className="text-xs mt-1 flex items-start gap-1" data-testid={`apt-address-${apt.id}`}>
                        <span className="text-primary/80 mt-0.5">📍</span>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(apt.service_address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary hover:text-amber-300 underline underline-offset-2 decoration-primary/40 hover:decoration-amber-300"
                          data-testid={`apt-maps-link-${apt.id}`}
                        >
                          {apt.service_address}
                        </a>
                        {apt.parking_instructions && <span className="text-muted-foreground"> · {apt.parking_instructions}</span>}
                      </p>
                    )}
                    {apptStaff.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-0.5">Staff: {apptStaff.map(s => s.first_name).join(', ')}</p>
                    )}
                    {checkedIn && !checkedOut && (
                      <p className="text-xs text-primary mt-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Checked-In · {new Date(apt.checked_in_at).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit'})}</p>
                    )}
                    {checkedOut && (
                      <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />Completed · ${apt.total_amount?.toFixed(2) || '0.00'} ({apt.payment_status?.replace(/_/g, ' ') || 'unpaid'})
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1.5 items-end">
                    <PrintLabelButton testId={`print-label-${apt.id}`} onClick={() => printIvBagLabel({
                      appointment: apt, patient: p, service: svc,
                      addOns: [...(apt.add_on_service_ids || []), ...(apt.services_rendered || [])].filter((id, i, a) => id !== apt.service_type_id && a.indexOf(id) === i).map(id => serviceById[id]?.name),
                      staffNames: apptStaff.map(s => `${s.first_name} ${s.last_name}`), preparedBy: user?.name,
                    })} />
                    {apt.confirmed === false && !checkedIn && !checkedOut && (
                      <Button size="sm" onClick={() => handleConfirm(apt)} data-testid={`confirm-${apt.id}`}
                        className="bg-orange-500 hover:bg-orange-600 text-white font-semibold text-xs h-7">
                        <CheckCircle2 className="w-3 h-3 mr-1" />Confirm
                      </Button>
                    )}
                    {!checkedIn && !checkedOut && (
                      <Button size="sm" onClick={() => handleCheckIn(apt)} data-testid={`check-in-${apt.id}`}
                        className="bg-primary/20 hover:bg-primary/30 text-primary border border-primary/40 text-xs h-7">
                        <LogInIcon className="w-3 h-3 mr-1" />Check In
                      </Button>
                    )}
                    {checkedIn && !checkedOut && (
                      <>
                        {user?.role !== 'management' && (
                          <Button size="sm" onClick={() => setCheckoutAppt(apt)} data-testid={`check-out-${apt.id}`}
                            className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold text-xs h-7">
                            <DollarSign className="w-3 h-3 mr-0.5" />Check Out
                          </Button>
                        )}
                        <button type="button" onClick={() => handleUndoCheckIn(apt)} className="text-[10px] text-muted-foreground hover:text-foreground underline">Undo check-in</button>
                      </>
                    )}
                    {checkedOut && (
                      <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-400 bg-emerald-500/10">✓ Completed</Badge>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {checkoutAppt && (
          <CheckoutModal
            isOpen={!!checkoutAppt}
            onClose={() => setCheckoutAppt(null)}
            appointment={checkoutAppt}
            patient={patientById[checkoutAppt.patient_id]}
            serviceTypes={serviceTypes}
            onComplete={() => { setCheckoutAppt(null); onRefresh(); }}
          />
        )}
      </CardContent>
    </Card>
  );
};

// ==================== CHECKOUT MODAL ====================
export const CheckoutModal = ({ isOpen, onClose, appointment, patient, serviceTypes, onComplete }) => {
  const [selected, setSelected] = useState(() => appointment?.service_type_id ? [appointment.service_type_id, ...(appointment.add_on_service_ids || []).filter(id => id !== appointment.service_type_id)] : []);
  const [deliveryFee, setDeliveryFee] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState('paid_at_checkout');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const [credits, setCredits] = useState([]);
  const selectedServices = useMemo(() => serviceTypes.filter(s => selected.includes(s.id)), [selected, serviceTypes]);
  const servicesSubtotal = useMemo(() => selectedServices.reduce((sum, s) => sum + (s.price || 0), 0), [selectedServices]);
  const membershipDiscount = useMemo(() => credits.reduce((sum, c) => sum + (serviceTypes.find(s => s.id === c.service_type_id)?.price || 0), 0), [credits, serviceTypes]);
  const coveredIds = useMemo(() => new Set(credits.map(c => c.service_type_id)), [credits]);
  const referralDiscount = useMemo(() => Math.min(Number(appointment?.referral_discount || 0), Math.max(0, servicesSubtotal - membershipDiscount)), [appointment, servicesSubtotal, membershipDiscount]);

  const deliveryAmount = deliveryFee ? DELIVERY_FEE_AMOUNT : 0;
  const total = useMemo(() => Math.round((Math.max(0, servicesSubtotal - membershipDiscount - referralDiscount) + deliveryAmount) * 100) / 100, [servicesSubtotal, membershipDiscount, referralDiscount, deliveryAmount]);

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/appointments/${appointment.id}/check-out`, {
        services_rendered: selected,
        delivery_fee: deliveryAmount,
        payment_status: paymentStatus,
        payment_method: paymentStatus === 'paid_ahead' ? null : paymentMethod,
        checkout_notes: notes || null,
        membership_credits: credits,
      });
      toast.success(`Checked out · Total: $${total.toFixed(2)}`);
      if (res.data?.referral_reward) toast.success(`Referral reward granted: ${res.data.referral_reward.member_name} earned a free IM injection`, { duration: 8000 });
      onComplete();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Checkout failed'); }
    finally { setLoading(false); }
  };

  const byCategory = useMemo(() => {
    const groups = { hydration: [], iv_drip: [], injection: [], other: [] };
    serviceTypes.forEach(s => {
      const cat = s.category || 'other';
      (groups[cat] || groups.other).push(s);
    });
    return groups;
  }, [serviceTypes]);

  const categoryLabels = {
    hydration: 'Hydration',
    iv_drip: 'IV Drips',
    injection: 'Injections',
    other: 'Other Services',
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Check Out · {patient?.first_name} {patient?.last_name}</DialogTitle>
          <DialogDescription>Select all services rendered. Total auto-calculates.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {Object.entries(byCategory).map(([cat, items]) => items.length > 0 && (
            <div key={cat}>
              <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">{categoryLabels[cat]}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {items.map(s => {
                  const isSelected = selected.includes(s.id);
                  return (
                    <button key={s.id} type="button" onClick={() => toggle(s.id)}
                      data-testid={`checkout-svc-${s.id}`}
                      className={`text-left p-2.5 rounded-lg border transition-all flex items-center justify-between gap-2 ${
                        isSelected ? 'border-primary bg-primary/15' : 'border-border/40 bg-muted/20 hover:border-primary/40'
                      }`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${isSelected ? 'bg-primary border-primary' : 'border-border'}`}>
                          {isSelected && <CheckCircle2 className="w-3 h-3 text-primary-foreground" />}
                        </div>
                        <span className="text-sm truncate">{s.name}</span>
                      </div>
                      <span className={`text-sm font-semibold ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>${s.price?.toFixed(2) || '0.00'}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <CheckoutMembershipSection patientId={patient?.id} selectedServices={selectedServices} applied={credits} onChange={setCredits} />

          {/* Delivery fee toggle */}
          <div className={`flex items-center justify-between p-3 rounded-lg border transition-all ${deliveryFee ? 'border-primary/40 bg-primary/10' : 'border-border/40 bg-muted/20'}`} data-testid="delivery-fee-row">
            <div className="flex items-center gap-3">
              <Switch checked={deliveryFee} onCheckedChange={setDeliveryFee} data-testid="delivery-fee-toggle" />
              <div>
                <p className="text-sm font-medium">Mobile / Delivery Service</p>
                <p className="text-[11px] text-muted-foreground">Turn off if this was an in-clinic session.</p>
              </div>
            </div>
            <span className={`text-sm font-semibold ${deliveryFee ? 'text-primary' : 'text-muted-foreground line-through'}`}>+${DELIVERY_FEE_AMOUNT.toFixed(2)}</span>
          </div>

          {/* Itemized breakdown + Total */}
          <div className="bg-gradient-to-r from-primary/15 via-amber-500/5 to-transparent border border-primary/30 rounded-xl p-4 space-y-2" data-testid="checkout-breakdown">
            <div className="space-y-1 pb-2 border-b border-primary/20" data-testid="checkout-items">
              {selectedServices.length === 0 && <p className="text-xs text-muted-foreground italic">No services selected</p>}
              {selectedServices.map(s => {
                const isPrimary = s.id === appointment?.service_type_id;
                const isAddOn = (appointment?.add_on_service_ids || []).includes(s.id);
                const covered = coveredIds.has(s.id);
                return (
                  <div key={s.id} className="flex items-center justify-between text-xs" data-testid={`checkout-item-${s.id}`}>
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="truncate">{s.name}</span>
                      {isPrimary && <span className="text-[9px] uppercase tracking-wider text-muted-foreground border border-border/50 rounded px-1">booked</span>}
                      {isAddOn && <span className="text-[9px] uppercase tracking-wider text-primary border border-primary/40 rounded px-1">add-on</span>}
                      {covered && <span className="text-[9px] uppercase tracking-wider text-emerald-300 border border-emerald-500/40 rounded px-1">member credit</span>}
                    </span>
                    <span className={covered ? 'line-through text-muted-foreground' : ''}>${(s.price || 0).toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Services subtotal ({selected.length} item{selected.length === 1 ? '' : 's'})</span>
              <span className="font-medium" data-testid="checkout-subtotal">${servicesSubtotal.toFixed(2)}</span>
            </div>
            {membershipDiscount > 0 && (
              <div className="flex items-center justify-between text-sm text-emerald-300">
                <span>Elite Membership credit{credits.length > 1 ? 's' : ''} ({credits.length})</span>
                <span className="font-medium" data-testid="checkout-membership-discount">−${membershipDiscount.toFixed(2)}</span>
              </div>
            )}
            {referralDiscount > 0 && (
              <div className="flex items-center justify-between text-sm text-sky-300">
                <span>Referral discount (friend of Elite Member)</span>
                <span className="font-medium" data-testid="checkout-referral-discount">−${referralDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Delivery fee</span>
              <span className="font-medium" data-testid="checkout-delivery">${deliveryAmount.toFixed(2)}</span>
            </div>
            <div className="border-t border-primary/30 pt-2 flex items-center justify-between">
              <span className="text-sm text-muted-foreground uppercase tracking-widest">Total</span>
              <span className="text-3xl font-bold gold-gradient-text" data-testid="checkout-total">${total.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment */}
          <div>
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">Payment</p>
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { id: 'paid_ahead', label: '✓ Patient Pre-Paid', sub: 'Skip payment step' },
                  { id: 'paid_at_checkout', label: 'Collecting Now', sub: 'Card / Cash / Other' },
                  { id: 'unpaid', label: 'Bill Later', sub: 'Outstanding balance' },
                ].map(opt => (
                  <button key={opt.id} type="button" onClick={() => setPaymentStatus(opt.id)}
                    data-testid={`pay-status-${opt.id}`}
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      paymentStatus === opt.id ? 'border-primary bg-primary/15' : 'border-border/40 bg-muted/20 hover:border-primary/40'
                    }`}>
                    <p className="text-sm font-medium">{opt.label}</p>
                    <p className="text-[10px] text-muted-foreground">{opt.sub}</p>
                  </button>
                ))}
              </div>

              {paymentStatus === 'paid_at_checkout' && (
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger><SelectValue placeholder="Payment method" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="card">Credit / Debit Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="external">External Payment App (Venmo, Cash App, etc.)</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <Label>Checkout Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional notes..." />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || selected.length === 0} data-testid="confirm-checkout-btn"
            className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold">
            {loading ? 'Processing...' : `Complete Checkout · $${total.toFixed(2)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== BIG CALENDAR (Side-by-side view) ====================
